import { resolveUploadImageUrl } from 'api/utils/resolveUploadImageUrl'

export const PHOTO_CATEGORY_KEYS = [
  'livingRoom1',
  'livingRoom2',
  'kitchen',
  'bedroom1',
  'bedroom2',
  'bedroom3',
  'bathroom',
  'bathroom2',
  'balcony',
  'exterior',
  'building',
  'other',
] as const

export type ListingPhotoCategory = (typeof PHOTO_CATEGORY_KEYS)[number]

export interface ListingPhoto {
  id: string
  url: string
  /** Empty until the user picks a room from the select. */
  category: ListingPhotoCategory | ''
}

export function createListingPhotoId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `photo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function isPhotoCategory(value: unknown): value is ListingPhotoCategory {
  return typeof value === 'string' && (PHOTO_CATEGORY_KEYS as readonly string[]).includes(value)
}

function resolvePhotoCategory(value: unknown): ListingPhotoCategory | '' {
  if (typeof value === 'string' && value === '') return ''
  return isPhotoCategory(value) ? value : 'other'
}

export function flattenPhotosByCategory(photos: ListingPhoto[]): ListingPhoto[] {
  const withCategory = photos.map(p => ({
    ...p,
    category: (p.category || 'other') as ListingPhotoCategory,
  }))
  return PHOTO_CATEGORY_KEYS.flatMap(category =>
    withCategory.filter(p => p.category === category)
  )
}

export function listingPhotosToFlatUrls(photos: ListingPhoto[]): string[] {
  return flattenPhotosByCategory(photos)
    .map(p => resolveUploadImageUrl(p.url))
    .filter(url => url !== '')
}

export function legacyUrlsToPhotos(urls: string[]): ListingPhoto[] {
  return urls
    .filter((url): url is string => typeof url === 'string' && url.trim() !== '')
    .map(url => ({
      id: createListingPhotoId(),
      url: resolveUploadImageUrl(url),
      category: 'other' as const,
    }))
    .filter(photo => photo.url !== '')
}

export function normalizeListingPhotos(raw: unknown): ListingPhoto[] {
  if (!Array.isArray(raw)) return []

  const parsed: ListingPhoto[] = []
  raw.forEach(item => {
    if (!item || typeof item !== 'object') return
    const record = item as Record<string, unknown>
    const url = typeof record.url === 'string' ? record.url.trim() : ''
    if (!url) return
    const category = resolvePhotoCategory(record.category)
    const id =
      typeof record.id === 'string' && record.id.trim() !== ''
        ? record.id.trim()
        : createListingPhotoId()
    parsed.push({ id, url: resolveUploadImageUrl(url), category })
  })

  return parsed.filter(photo => photo.url !== '')
}

export function hydrateListingPhotos(
  metaPhotos: unknown,
  legacyImages: unknown
): ListingPhoto[] {
  const fromMeta = normalizeListingPhotos(metaPhotos)
  if (fromMeta.length > 0) return fromMeta

  if (Array.isArray(legacyImages)) {
    return legacyUrlsToPhotos(legacyImages)
  }
  return []
}

export function serializeListingPhotos(photos: ListingPhoto[]): ListingPhoto[] {
  return flattenPhotosByCategory(photos)
    .map(({ id, url, category }) => ({
      id,
      url: resolveUploadImageUrl(url),
      category,
    }))
    .filter(photo => photo.url !== '')
}

export function getPhotosForCategory(
  photos: ListingPhoto[],
  category: ListingPhotoCategory
): ListingPhoto[] {
  return photos.filter(p => p.category === category)
}

export function movePhotoInCategory(
  photos: ListingPhoto[],
  category: ListingPhotoCategory,
  photoId: string,
  direction: -1 | 1
): ListingPhoto[] {
  const inCategory = getPhotosForCategory(photos, category)
  const index = inCategory.findIndex(p => p.id === photoId)
  if (index < 0) return photos

  const targetIndex = index + direction
  if (targetIndex < 0 || targetIndex >= inCategory.length) return photos

  const reordered = [...inCategory]
  ;[reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]]

  const others = photos.filter(p => p.category !== category)
  return flattenPhotosByCategory([...others, ...reordered])
}

export function removeListingPhoto(photos: ListingPhoto[], photoId: string): ListingPhoto[] {
  return photos.filter(p => p.id !== photoId)
}

export function changeListingPhotoCategory(
  photos: ListingPhoto[],
  photoId: string,
  category: ListingPhotoCategory | ''
): ListingPhoto[] {
  return photos.map(p => (p.id === photoId ? { ...p, category } : p))
}

export function movePhotoInList(
  photos: ListingPhoto[],
  photoId: string,
  direction: -1 | 1
): ListingPhoto[] {
  const index = photos.findIndex(p => p.id === photoId)
  if (index < 0) return photos

  const targetIndex = index + direction
  if (targetIndex < 0 || targetIndex >= photos.length) return photos

  return reorderPhotosInList(photos, index, targetIndex)
}

export function reorderPhotosInList(
  photos: ListingPhoto[],
  fromIndex: number,
  toIndex: number
): ListingPhoto[] {
  if (fromIndex === toIndex) return photos
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= photos.length ||
    toIndex >= photos.length
  ) {
    return photos
  }

  const next = [...photos]
  const [removed] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, removed)
  return next
}

export function reorderPhotosByIds(
  photos: ListingPhoto[],
  draggedId: string,
  targetId: string,
  categoryFilter?: ListingPhotoCategory | ''
): ListingPhoto[] {
  if (draggedId === targetId) return photos

  const scope =
    categoryFilter !== undefined
      ? photos.filter(p => p.category === categoryFilter)
      : photos

  const fromIndex = scope.findIndex(p => p.id === draggedId)
  const toIndex = scope.findIndex(p => p.id === targetId)
  if (fromIndex < 0 || toIndex < 0) return photos

  const reorderedScope = reorderPhotosInList(scope, fromIndex, toIndex)

  if (categoryFilter !== undefined) {
    let scopeIndex = 0
    return photos.map(photo =>
      photo.category === categoryFilter ? reorderedScope[scopeIndex++] : photo
    )
  }

  return reorderedScope
}

export function appendListingPhotos(
  photos: ListingPhoto[],
  additions: ListingPhoto[]
): ListingPhoto[] {
  return [...photos, ...additions]
}
